import ButtonLink from '@components/button-link/ButtonLink';
import LikeButton from '@components/buttons/LikeButton';
import PrimaryImage from '@components/primary-image/PrimaryImage';
import useDocument from '@hooks/useDocument';
import Sentry from '@integrations/Sentry';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import UserAvatar from '@ui/components/user-avatar/UserAvatar';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import shortAddress from '@ui/utils/shortAddress';
import slugOrID from '@ui/utils/slugOrID';
import staticMap from '@ui/utils/staticMap';
import { homeToSegmentEventData } from '@utils/segmentAnalytics';
import Link from 'next/link';
import { parseAddress } from 'parse-address';
import React, { CSSProperties, useMemo } from 'react';

import useStyles from './HomeCardWide.style';

interface HomeCardWideProps {
  home?: Partial<guesthouse.Home>;
  imageHeight?: string | number;
  style?: CSSProperties;
  className?: string;
  onClick?: (home: Partial<guesthouse.Home>) => void;
  saved?: boolean;
  onSavedClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  imageFallback?: string | null;
}

const HomeCardWide: React.FC<HomeCardWideProps> = (props) => {
  const {
    className,
    home,
    onClick,
    style,
    saved,
    onSavedClick,
    imageFallback,
  } = props;
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const styleObj: CSSProperties = { ...style };
  const { md } = useBreakpoints();

  if (typeof onClick === 'function') {
    styleObj.cursor = 'pointer';
  }

  const { doc: homeDoc } = useDocument(`homes/${home.docID}`);

  const fullHome = useMemo(() => {
    if (homeDoc?.exists()) {
      return homeDoc.data() as guesthouse.Home;
    }
  }, [homeDoc]);

  const { city, state } = useMemo(() => {
    if (home.address) {
      return parseAddress(home.address.replace(', USA', ''));
    }

    if (fullHome.address) {
      return parseAddress(fullHome.address.replace(', USA', ''));
    }

    return {
      city: '',
      state: '',
    };
  }, [home, fullHome]);

  const isCaseStudy = useMemo(() => {
    return (fullHome || home)?.case_study === true;
  }, [fullHome, home]);

  const caseStudyTitle = useMemo(() => {
    return (fullHome || home)?.case_study_title;
  }, [fullHome, home]);

  const homeLink = useMemo(() => {
    if (isCaseStudy) {
      return `/case-studies/${slugOrID(home)}`;
    }
    return `/shop/homes/${slugOrID(home)}`;
  }, [isCaseStudy, home]);

  return (
    <div
      className={cx(classes.container, className)}
      style={styleObj}
      onClick={() => typeof onClick === 'function' && onClick(home)}
    >
      <div className={classes.imageWrapper}>
        <Link href={homeLink}>

          <PrimaryImage
            item={fullHome || home}
            fallback={imageFallback === null ? undefined : (imageFallback !== undefined ? imageFallback : staticMap(home.address))}
            className={classes.image}
          />

        </Link>

        <LikeButton
          className={classes.likeButton}
          large={md}
          enabled={saved}
          variant="outlined"
          onClick={onSavedClick}
        />
      </div>

      <div className={classes.content}>
        {isCaseStudy && caseStudyTitle ? (
          <>
            <Typography
              variant="body2"
              component="address"
              style={{
                fontStyle: 'normal',
                marginBottom: theme.spacing(2),
                color: theme.palette.text.secondary,
                fontSize: 13,
                lineHeight: 1.6,
                letterSpacing: '0.02em',
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            >
              {shortAddress((home || fullHome).address)}
              {(home || fullHome).unit && `, Unit ${(home || fullHome).unit}`}
              {city && state && (
                <>
                  <br />
                  {city}
                  ,
                  {' '}
                  {state}
                </>
              )}
            </Typography>
            <Typography
              variant="h3Alt"
              component="h3"
              className={classes.address}
              style={{ fontStyle: 'normal' }}
            >
              {caseStudyTitle}
            </Typography>
          </>
        ) : (
          <Typography
            variant="h3Alt"
            component="address"
            className={classes.address}
            style={{ fontStyle: 'normal' }}
          >
            {shortAddress((home || fullHome).address)}
            {(home || fullHome).unit && `, Unit ${(home || fullHome).unit}`}
            <br />
            {city}
            ,
            {' '}
            {state}
          </Typography>
        )}

        {(home || fullHome).description ?
          <Typography style={{ fontSize: 14 }}>
            {(home || fullHome).description}
          </Typography>
          : null}

        {(home || fullHome).realtor && (
          <div className={classes.realtorInfo}>
            <UserAvatar
              size={35}
              user={(home || fullHome).realtor}
              className={classes.realtorIcon}
              avatarStyle={{
                fontSize: 14,
              }}
            />

            <Typography className={classes.realtor}>
              Listed by
              {' '}
              {home.realtor.firstname}
              {' '}
              {home.realtor.lastname}
            </Typography>
          </div>
        )}

        <Button
          variant="contained"
          color="secondary"
          className={classes.button}
          component={ButtonLink}
          href={homeLink}
          onClick={() => {
            try {
              window.analytics?.track('Home Clicked', homeToSegmentEventData(fullHome || home));
            } catch (e) {
              Sentry.captureException(e);
            }
          }}
        >
          {isCaseStudy ? 'Read More' : 'Shop Now'}
        </Button>
      </div>
    </div>
  );
};

export default HomeCardWide;
